import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  X, Upload, Sparkles, Loader2, AlertCircle, CheckCircle2, 
  Trash2, Package, ArrowRight, RefreshCw, ZoomIn, Copy, AlertTriangle,
  Plus, Image as ImageIcon, FileSpreadsheet, Link, FileText, Table, ExternalLink,
  Search, Check, ChevronDown, Filter
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { generateContentWithFallback } from '@/lib/geminiClient';
import { toast } from 'sonner';
import { useSession } from '@/lib/SessionContext';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n || 0);

// Helper JSONP loader for Google Visualization API to bypass all CORS completely
const fetchGoogleSheetJsonp = (sheetId, gid = '0') => {
  return new Promise((resolve, reject) => {
    const callbackName = 'gviz_cb_' + Math.random().toString(36).substring(2, 9);
    const script = document.createElement('script');
    
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Waktu permintaan Google Sheets habis (Timeout). Pastikan link benar dan akses disetel Publik.'));
    }, 15000);

    const cleanup = () => {
      clearTimeout(timeout);
      delete window[callbackName];
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };

    window[callbackName] = (response) => {
      cleanup();
      if (!response) {
        reject(new Error('Respon Google Sheets kosong.'));
        return;
      }
      if (response.status !== 'ok') {
        const errMsg = response?.errors?.[0]?.message || response?.errors?.[0]?.detailed_message || 'Gagal membaca sheet.';
        reject(new Error(errMsg));
        return;
      }
      resolve(response.table);
    };

    script.src = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=responseHandler:${callbackName}&gid=${gid}&headers=1`;
    script.onerror = () => {
      cleanup();
      reject(new Error('Gagal memuat Google Spreadsheet. Pastikan link dapat diakses publik ("Anyone with the link can view").'));
    };

    document.body.appendChild(script);
  });
};

const convertGvizTableToText = (table) => {
  if (!table) return '';
  const headers = (table.cols || []).map((c, i) => c.label || c.id || `Kolom_${i + 1}`);
  const rowsText = (table.rows || []).map(r => {
    if (!r || !r.c) return '';
    return r.c.map(cell => {
      if (!cell) return '';
      return String(cell.f !== undefined ? cell.f : (cell.v !== null && cell.v !== undefined ? cell.v : '')).trim();
    }).join(' | ');
  }).filter(line => line.replace(/\|/g, '').trim().length > 0);

  return [headers.join(' | '), ...rowsText].join('\n');
};

// Fast local deterministic parser for standard tabular sheet data with multi-column intelligence
const parseStructuredTableDirectly = (tableText) => {
  if (!tableText) return [];
  const rawLines = tableText.split('\n').map(l => l.trim()).filter(Boolean);
  if (rawLines.length < 1) return [];

  const splitRow = (line) => {
    let cells = [];
    if (line.includes(' | ')) {
      cells = line.split(' | ');
    } else if (line.includes('\t')) {
      cells = line.split('\t');
    } else if (line.includes('","') || line.startsWith('"')) {
      cells = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(c => c.replace(/^"|"$/g, ''));
    } else {
      cells = line.split(',');
    }
    return cells.map(c => (c || '').trim());
  };

  const allRows = rawLines.map(splitRow);
  if (allRows.length === 0) return [];

  // 1. Scan the first 10 rows to detect header row & columns
  let headerRowIdx = -1;
  let nameColIdx = -1;
  let sizeColIdx = -1;
  let qtyColIdx = -1;
  let catatanColIdx = -1;

  for (let r = 0; r < Math.min(10, allRows.length); r++) {
    const row = allRows[r];
    const cleanCells = row.map(c => c.toLowerCase().replace(/[^a-z0-9]/g, ''));
    const isCandidate = cleanCells.some(h => 
      ['nama', 'varian', 'variant', 'produk', 'product', 'item', 'ukuran', 'size', 'ml', 'qty', 'jumlah', 'pcs', 'kuantiti', 'order', 'pesanan'].some(kw => h.includes(kw))
    );

    if (isCandidate) {
      headerRowIdx = r;
      cleanCells.forEach((h, idx) => {
        if (['nama', 'varian', 'variant', 'produk', 'product', 'item'].some(kw => h.includes(kw)) && !h.includes('ukuran') && !h.includes('qty')) {
          if (nameColIdx === -1) nameColIdx = idx;
        } else if (['ukuran', 'size', 'ml', 'vol'].some(kw => h.includes(kw))) {
          if (sizeColIdx === -1) sizeColIdx = idx;
        } else if (['qty', 'jumlah', 'kuantiti', 'pcs', 'order', 'pesanan', 'total', 'count'].some(kw => h.includes(kw))) {
          if (qtyColIdx === -1) qtyColIdx = idx;
        } else if (['catatan', 'keterangan', 'note', 'notes', 'remark'].some(kw => h.includes(kw))) {
          if (catatanColIdx === -1) catatanColIdx = idx;
        }
      });
      break;
    }
  }

  const startRow = headerRowIdx !== -1 ? headerRowIdx + 1 : 0;
  const dataRows = allRows.slice(startRow).filter(r => r.some(c => c && c.length > 0 && c !== '/' && c !== '-'));
  if (dataRows.length === 0) return [];

  // 2. If nameColIdx or qtyColIdx still not found, score columns dynamically
  if (nameColIdx === -1 || qtyColIdx === -1) {
    const maxCols = Math.max(...dataRows.map(r => r.length));
    const colScores = [];

    for (let c = 0; c < maxCols; c++) {
      let textCount = 0;
      let pureNumberCount = 0;
      let sizeLikeCount = 0;
      let indexLikeCount = 0;

      dataRows.forEach((row, rowIdx) => {
        const val = (row[c] || '').trim();
        if (!val || val === '-' || val === '/' || val === '|') return;

        const num = parseFloat(val.replace(/[^0-9.]/g, ''));
        const hasLetters = /[a-zA-Z]/.test(val);
        const hasSizeUnit = /ml\b|gr\b|liter\b/i.test(val);

        if (hasSizeUnit || ([10, 15, 20, 25, 30, 35, 50, 58, 60, 100, 250].includes(num) && val.length <= 6)) {
          sizeLikeCount++;
        } else if (!hasLetters && !isNaN(num) && num > 0) {
          pureNumberCount++;
          if (num === rowIdx + 1) indexLikeCount++;
        } else if (hasLetters && val.length >= 2) {
          textCount++;
        }
      });

      colScores.push({ colIdx: c, textCount, pureNumberCount, sizeLikeCount, indexLikeCount });
    }

    if (nameColIdx === -1) {
      const bestTextCol = [...colScores].sort((a, b) => b.textCount - a.textCount)[0];
      if (bestTextCol && bestTextCol.textCount > 0) {
        nameColIdx = bestTextCol.colIdx;
      }
    }

    if (sizeColIdx === -1) {
      const bestSizeCol = [...colScores]
        .filter(cs => cs.colIdx !== nameColIdx)
        .sort((a, b) => b.sizeLikeCount - a.sizeLikeCount)[0];
      if (bestSizeCol && bestSizeCol.sizeLikeCount > dataRows.length * 0.15) {
        sizeColIdx = bestSizeCol.colIdx;
      }
    }

    if (qtyColIdx === -1) {
      const bestQtyCol = [...colScores]
        .filter(cs => cs.colIdx !== nameColIdx && cs.colIdx !== sizeColIdx)
        .sort((a, b) => (b.pureNumberCount - b.indexLikeCount) - (a.pureNumberCount - a.indexLikeCount))[0];
      if (bestQtyCol && bestQtyCol.pureNumberCount > 0) {
        qtyColIdx = bestQtyCol.colIdx;
      }
    }
  }

  // If we still can't identify a valid variant name column, trigger AI fallback
  if (nameColIdx === -1) return [];

  const parsedItems = [];

  for (const row of dataRows) {
    let rawName = (row[nameColIdx] || '').trim();
    // Clean leading/trailing artifacts
    rawName = rawName.replace(/^[|/\s\-:,]+|[|/\s\-:,]+$/g, '').trim();

    // Skip empty or purely non-alphabetic garbage
    if (!rawName || rawName.length < 2 || !/[a-zA-Z]/.test(rawName)) {
      continue;
    }

    let sizeMl = 20;
    if (sizeColIdx !== -1 && row[sizeColIdx]) {
      const extractedSize = parseInt(String(row[sizeColIdx]).replace(/[^0-9]/g, '')) || 0;
      if (extractedSize > 0) sizeMl = extractedSize;
    } else {
      const sizeMatch = rawName.match(/(\d+)\s*ml/i);
      if (sizeMatch) {
        sizeMl = parseInt(sizeMatch[1]);
      } else {
        const trailingNumMatch = rawName.match(/\b(10|15|20|25|30|35|50|58|60|100|250)\b/);
        if (trailingNumMatch) {
          sizeMl = parseInt(trailingNumMatch[1]);
        }
      }
    }

    let qtyNum = 1;
    if (qtyColIdx !== -1 && row[qtyColIdx]) {
      qtyNum = parseInt(String(row[qtyColIdx]).replace(/[^0-9]/g, '')) || 0;
    } else {
      // Look for first available number column
      const numCell = row.find((c, idx) => idx !== nameColIdx && idx !== sizeColIdx && /^\d+$/.test((c || '').trim()));
      if (numCell) qtyNum = parseInt(numCell.trim()) || 1;
    }

    let catatan = '';
    if (catatanColIdx !== -1 && row[catatanColIdx]) {
      catatan = String(row[catatanColIdx]).replace(/^[|/\s\-:,]+|[|/\s\-:,]+$/g, '').trim();
    } else {
      const otherCols = row.filter((c, idx) => idx !== nameColIdx && idx !== sizeColIdx && idx !== qtyColIdx && c && c.trim().length > 0 && c.trim() !== '/' && c.trim() !== '-');
      if (otherCols.length > 0) {
        catatan = otherCols.join(' | ').replace(/^[|/\s\-:,]+|[|/\s\-:,]+$/g, '').trim();
      }
    }

    let formattedRawName = rawName;
    if (!formattedRawName.toLowerCase().includes('ml')) {
      formattedRawName = `${rawName} ${sizeMl}ml`;
    }

    if (rawName && qtyNum > 0) {
      parsedItems.push({
        raw_variant_name: formattedRawName,
        ukuran_ml: sizeMl,
        qty: qtyNum,
        catatan
      });
    }
  }

  // Quality check: make sure at least 50% have realistic names with letters
  const validNamed = parsedItems.filter(p => p.raw_variant_name && p.raw_variant_name.replace(/(\d+)\s*ml/gi, '').trim().length >= 2);
  if (validNamed.length === 0 || validNamed.length < parsedItems.length * 0.4) {
    return [];
  }

  return parsedItems;
};

// High Performance Single Row Component (Memoized for 60fps)
const ScannedRowItem = memo(({ 
  item, 
  varianDatabase, 
  onToggleSelect, 
  onOpenPicker,
  onSizeChange, 
  onQtyChange, 
  onPriceChange,
  onRemove 
}) => {
  const varianObj = varianDatabase.find(v => v.id === item.matchedVariantId);
  const availableSizes = varianObj?.sizes || [];
  const unitPrice = item.productData?.harga_jual_per_pcs || 0;
  const subtotal = unitPrice * (Number(item.qty) || 1);

  return (
    <tr className={`hover:bg-[#F9F5EC]/40 transition-colors ${!item.selected ? 'opacity-40 bg-slate-50/50' : ''} ${!item.isMatched ? 'bg-amber-50/30' : ''}`}>
      <td className="p-3 text-center">
        <input
          type="checkbox"
          checked={item.selected}
          onChange={() => onToggleSelect(item.id)}
          className="rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208] cursor-pointer w-4 h-4"
        />
      </td>
      <td className="p-3">
        <span className="font-bold text-slate-800 text-xs block">{item.rawName}</span>
        {item.catatan && (
          <span className="text-[10px] text-slate-400 italic block">{item.catatan}</span>
        )}
      </td>
      <td className="p-3">
        <div className="space-y-1.5 min-w-[220px]">
          <div className="flex items-center gap-1.5">
            {item.isMatched ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-md border border-emerald-300">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                Cocok di DB
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-md border border-amber-300">
                <AlertTriangle className="w-3 h-3 text-amber-600" />
                Belum Terdaftar
              </span>
            )}
          </div>

          {/* Clean Searchable Trigger Button */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenPicker(item.id);
            }}
            className={`w-full text-left flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all shadow-2xs group cursor-pointer ${
              item.isMatched 
                ? 'bg-white hover:bg-slate-50 border-slate-200 text-slate-800 hover:border-[#8C6208]/60' 
                : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 hover:border-amber-500 text-amber-950 font-bold shadow-xs'
            }`}
          >
            <span className="truncate">
              {varianObj ? varianObj.nama_product : '🔍 Cari & Pilih Varian DB...'}
            </span>
            <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${item.isMatched ? 'text-slate-400 group-hover:text-[#8C6208]' : 'text-amber-700'}`} />
          </button>
        </div>
      </td>
      <td className="p-3">
        {item.isMatched && availableSizes.length > 1 ? (
          <select
            value={item.rawSize}
            onChange={(e) => onSizeChange(item.id, e.target.value)}
            className="h-8 text-xs bg-white border border-slate-200 rounded-lg px-2 py-1 font-medium focus:ring-1 focus:ring-[#8C6208] outline-none shadow-2xs cursor-pointer"
          >
            {availableSizes.map(s => (
              <option key={s.size} value={s.size}>
                {s.size} ml
              </option>
            ))}
          </select>
        ) : (
          <Input
            type="number"
            min="1"
            value={item.rawSize}
            onChange={(e) => onSizeChange(item.id, e.target.value)}
            className="h-8 text-xs w-20 rounded-lg shadow-2xs"
          />
        )}
      </td>
      <td className="p-3">
        <Input
          type="number"
          min="1"
          value={item.qty}
          onChange={(e) => onQtyChange(item.id, e.target.value)}
          className="h-8 text-xs w-24 font-bold text-slate-900 rounded-lg shadow-2xs"
        />
      </td>
      <td className="p-3 text-right">
        <Input
          type="number"
          min="0"
          value={unitPrice || ''}
          placeholder="Rp 0"
          onChange={(e) => onPriceChange(item.id, e.target.value)}
          className="h-8 text-xs w-28 text-right font-semibold bg-white border-slate-200 rounded-lg shadow-2xs focus-visible:ring-[#8C6208]"
        />
      </td>
      <td className="p-3 text-right font-bold text-slate-800">
        {subtotal > 0 ? `Rp ${fmt(subtotal)}` : '-'}
      </td>
      <td className="p-3 text-center">
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="text-slate-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
});

export default function RepeatOrderScannerModal({
  isOpen,
  onClose,
  namaBrand,
  varianDatabase = [],
  botolList = [],
  tutupList = [],
  sprayList = [],
  bahanList = [],
  itemTambahanList = [],
  fullsetList = [],
  onApply
}) {
  const { member } = useSession();
  const fileInputRef = useRef(null);
  const appendFileInputRef = useRef(null);
  const excelFileInputRef = useRef(null);
  
  // Tab State: 'image' | 'spreadsheet'
  const [activeTab, setActiveTab] = useState('spreadsheet');

  // Image State
  const [imagePreviews, setImagePreviews] = useState([]);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Spreadsheet State
  const [sheetUrl, setSheetUrl] = useState('');
  const [pastedTableText, setPastedTableText] = useState('');
  const [sheetSourceInfo, setSheetSourceInfo] = useState('');

  // Processing & Data State
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scannedItems, setScannedItems] = useState([]);

  // Table Filter & Search State
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [tableFilterTab, setTableFilterTab] = useState('all'); // 'all' | 'matched' | 'unmatched'

  // Searchable Picker Modal State
  const [pickerActiveRowId, setPickerActiveRowId] = useState(null);
  const [pickerQuery, setPickerQuery] = useState('');
  const pickerInputRef = useRef(null);

  // Focus picker input on open
  useEffect(() => {
    if (pickerActiveRowId && pickerInputRef.current) {
      setTimeout(() => {
        pickerInputRef.current?.focus();
      }, 50);
    }
  }, [pickerActiveRowId]);

  // Global Clipboard Paste Listener (Ctrl+V)
  useEffect(() => {
    if (!isOpen) return;

    const handlePaste = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) return;

      const clipboardItems = e.clipboardData?.items;
      const textData = e.clipboardData?.getData('text/plain');

      if (clipboardItems) {
        for (let i = 0; i < clipboardItems.length; i++) {
          const item = clipboardItems[i];
          if (item.type.indexOf('image') !== -1) {
            const file = item.getAsFile();
            if (file) {
              e.preventDefault();
              setActiveTab('image');
              const shouldAppend = imagePreviews.length > 0;
              loadAndProcessFile(file, shouldAppend);
              toast.success(shouldAppend 
                ? 'Foto tambahan dari clipboard berhasil ditempel dan dianalisis!' 
                : 'Gambar dari clipboard (salin-tempel) berhasil dimuat!'
              );
              return;
            }
          }
        }
      }

      if (textData && (textData.includes('\t') || textData.includes('\n') || textData.includes('http'))) {
        if (textData.trim().startsWith('http') && textData.includes('docs.google.com/spreadsheets')) {
          e.preventDefault();
          setActiveTab('spreadsheet');
          setSheetUrl(textData.trim());
          processGoogleSheetUrl(textData.trim());
          toast.success('Link Google Spreadsheet dari clipboard berhasil dibaca!');
          return;
        }

        if (textData.includes('\t') || (textData.includes('\n') && textData.length > 10)) {
          e.preventDefault();
          setActiveTab('spreadsheet');
          setPastedTableText(textData);
          processSpreadsheetText(textData, 'Salinan Clipboard (Ctrl+V)');
          toast.success('Tabel dari clipboard berhasil dianalisis!');
          return;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen, varianDatabase, namaBrand, imagePreviews]);

  // Build product data helper for a matched variant & size
  const buildProductFromSize = (varian, selectedSizeStr, qty = 1) => {
    if (!varian) return null;

    if (varian.is_discovery_set) {
      const mappedItems = (varian.sizes || []).filter(s => s.racikan && s.racikan.length > 0).map(s => ({
        nama_variant: s.size,
        botol_id: s.botol_id || null,
        tutup_id: s.tutup_id || null,
        spray_id: s.spray_id || null,
        botol_dari_client: s.botol_dari_client,
        botol_ukuran_client: s.botol_ukuran_client,
        is_fullset: s.is_fullset || false,
        fullset_id: s.fullset_id || null,
        fullset_nama: s.fullset_nama || null,
        racikan: s.racikan || [],
      }));

      const mappedKomponen = (varian.sizes || []).filter(s => (!s.racikan || s.racikan.length === 0) && s.bahan).map(s => {
        const kName = s.bahan?.nama_bahan || s.size;
        const match = itemTambahanList.find(x => x.nama === kName);
        return {
          nama: match ? match.nama : kName,
          item_nama: match ? match.nama : kName,
          inventory_item_id: match ? match.id : null,
          kategori: match ? match.kategori : (s.bahan?.kategori_bahan || 'Box/Kemasan'),
          harga: match?.harga_rupiah || s.bahan?.custom_hpp || s.bahan?.harga_rupiah || 0,
          harga_jual: match?.harga_jual || s.bahan?.custom_harga_jual || s.bahan?.harga_jual || 0,
          qty: 1
        };
      }).filter(k => k.harga > 0 || k.harga_jual > 0);

      const attachedKomponen = (varian.sizes || []).flatMap(s => s.komponen_tambahan || []).map(k => {
        const match = itemTambahanList.find(x => x.nama === (k.nama || k.item_nama));
        return {
          ...k,
          inventory_item_id: match ? match.id : null,
          harga: match?.harga_rupiah || k.harga || 0,
          harga_jual: match?.harga_jual || k.harga_jual || 0,
        };
      });

      return {
        variant_id: varian.id,
        nama_product: varian.nama_product,
        is_discovery_set: true,
        qty: Number(qty) || 1,
        items: mappedItems,
        komponen_tambahan: [...mappedKomponen, ...attachedKomponen],
        botol_id: null,
        tutup_id: null,
        spray_id: null,
        fullset_id: null,
        is_fullset: false,
        botol_dari_client: false,
        botol_ukuran_client: 0,
        racikan: [],
        selected_size: 0,
        harga_jual_per_pcs: varian.harga_jual || 0,
        total_harga_jual: (varian.harga_jual || 0) * (Number(qty) || 1)
      };
    }

    const sizes = varian.sizes || [];
    let matchedSizeObj = sizes.find(s => String(s.size).toLowerCase() === String(selectedSizeStr).toLowerCase());
    if (!matchedSizeObj && sizes.length > 0) {
      const targetNum = parseFloat(selectedSizeStr);
      if (!isNaN(targetNum)) {
        matchedSizeObj = sizes.find(s => parseFloat(s.size) === targetNum) || sizes[0];
      } else {
        matchedSizeObj = sizes[0];
      }
    }

    const s = matchedSizeObj || {};
    const unitPrice = s.bahan?.custom_harga_jual || s.bahan?.harga_jual || s.harga_jual || varian.harga_jual || 0;

    return {
      variant_id: varian.id,
      nama_product: varian.nama_product,
      is_discovery_set: false,
      qty: Number(qty) || 1,
      selected_size: s.size || selectedSizeStr || 0,
      botol_id: s.botol_id || null,
      tutup_id: s.tutup_id || null,
      spray_id: s.spray_id || null,
      botol_dari_client: s.botol_dari_client ?? true,
      botol_ukuran_client: s.botol_ukuran_client || 0,
      is_fullset: s.is_fullset || false,
      fullset_id: s.fullset_id || null,
      fullset_nama: s.fullset_nama || null,
      racikan: s.racikan || [],
      komponen_tambahan: s.komponen_tambahan || [],
      harga_jual_per_pcs: unitPrice,
      total_harga_jual: unitPrice * (Number(qty) || 1)
    };
  };

  // Process rows extracted from AI JSON response
  const handleAIItemsResult = (items, sourceLabel = '', isAppend = false) => {
    if (!items || items.length === 0) {
      toast.warning('Tidak ditemukan daftar varian atau kuantiti pada dokumen tersebut.');
      return;
    }

    const processedRows = items.map((item, idx) => {
      const rawName = item.raw_variant_name || item.nama_variant || item.nama_produk || item.nama_product || '';
      const rawSize = item.ukuran_ml || item.size_ml || item.size || 20;
      const qty = Number(item.qty || item.jumlah || item.kuantiti) || 1;

      // Find match in varianDatabase with smart priority matching
      let matchedVarian = null;
      const cleanBrand = (namaBrand || '').toLowerCase().replace(/[^a-z0-9]/gi, '').trim();
      const cleanRaw = rawName.toLowerCase()
        .replace(/(\d+)\s*ml/gi, '')
        .replace(/[^a-z0-9]/gi, '')
        .trim();
      const rawWithoutBrand = cleanBrand && cleanRaw.startsWith(cleanBrand) ? cleanRaw.slice(cleanBrand.length) : cleanRaw;

      // 1. Direct match with AI suggestion if available
      if (item.matched_variant_name) {
        matchedVarian = varianDatabase.find(v => 
          v.nama_product.toLowerCase().trim() === item.matched_variant_name.toLowerCase().trim()
        );
      }

      // 2. Strict Exact match (case & whitespace & symbol insensitive)
      if (!matchedVarian) {
        matchedVarian = varianDatabase.find(v => {
          const cleanDb = v.nama_product.toLowerCase().replace(/[^a-z0-9]/gi, '').trim();
          const dbWithoutBrand = cleanBrand && cleanDb.startsWith(cleanBrand) ? cleanDb.slice(cleanBrand.length) : cleanDb;
          return cleanRaw === cleanDb || rawWithoutBrand === dbWithoutBrand || cleanRaw === dbWithoutBrand || rawWithoutBrand === cleanDb;
        });
      }

      // 3. Exact word-for-word match (regardless of order or small differences)
      if (!matchedVarian) {
        const rawWords = rawName.toLowerCase().replace(/(\d+)\s*ml/gi, '').split(/[^a-z0-9]+/i).filter(w => w && w !== cleanBrand);
        matchedVarian = varianDatabase.find(v => {
          const dbWords = v.nama_product.toLowerCase().split(/[^a-z0-9]+/i).filter(w => w && w !== cleanBrand);
          if (rawWords.length === 0 || dbWords.length === 0) return false;
          return (rawWords.length === dbWords.length && rawWords.every((w, i) => w === dbWords[i])) ||
                 (rawWords.every(w => dbWords.includes(w)) && dbWords.every(w => rawWords.includes(w)));
        });
      }

      // 4. Substring / Contains match for robust matching
      if (!matchedVarian && cleanRaw.length >= 3) {
        matchedVarian = varianDatabase.find(v => {
          const cleanDb = v.nama_product.toLowerCase().replace(/[^a-z0-9]/gi, '').trim();
          const dbWithoutBrand = cleanBrand && cleanDb.startsWith(cleanBrand) ? cleanDb.slice(cleanBrand.length) : cleanDb;
          return (cleanDb.length >= 3 && cleanRaw.includes(cleanDb)) ||
                 (rawWithoutBrand.length >= 3 && dbWithoutBrand.includes(rawWithoutBrand)) ||
                 (dbWithoutBrand.length >= 3 && rawWithoutBrand.includes(dbWithoutBrand));
        });
      }

      // If not strictly exact, do NOT guess. Leave as unmatched (Belum Terdaftar) for human verification!

      const isMatched = !!matchedVarian;
      const builtProduct = isMatched ? buildProductFromSize(matchedVarian, rawSize, qty) : null;

      return {
        id: `scan-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`,
        selected: true,
        rawName,
        rawSize,
        qty,
        catatan: item.catatan || '',
        isMatched,
        matchedVariantId: matchedVarian?.id || '',
        productData: builtProduct
      };
    });

    // Sort rows alphabetically A-Z by variant name, then ascending by size (ml)
    const getBaseName = (row) => (row.rawName || '').replace(/(\d+)\s*ml/gi, '').trim();
    processedRows.sort((a, b) => {
      const baseA = getBaseName(a);
      const baseB = getBaseName(b);
      const nameComp = baseA.localeCompare(baseB, 'id', { sensitivity: 'base' });
      if (nameComp !== 0) return nameComp;

      const sizeA = parseFloat(a.rawSize) || 0;
      const sizeB = parseFloat(b.rawSize) || 0;
      return sizeA - sizeB;
    });

    if (isAppend) {
      setScannedItems(prev => {
        const combined = [...prev, ...processedRows];
        combined.sort((a, b) => {
          const baseA = getBaseName(a);
          const baseB = getBaseName(b);
          const nameComp = baseA.localeCompare(baseB, 'id', { sensitivity: 'base' });
          if (nameComp !== 0) return nameComp;
          return (parseFloat(a.rawSize) || 0) - (parseFloat(b.rawSize) || 0);
        });
        return combined;
      });
      toast.success(`+ ${processedRows.length} varian baru berhasil ditambahkan!`);
    } else {
      setScannedItems(processedRows);
      toast.success(`Berhasil memuat ${processedRows.length} baris pesanan dari ${sourceLabel || 'dokumen'}! 🎉`);
    }
  };

  // Process image with AI
  const processImageWithAI = async (file, isAppend = false) => {
    setIsProcessing(true);
    if (!isAppend) {
      setScannedItems([]);
    }

    try {
      const base64EncodedDataPromise = new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
      });
      const inlineData = {
        inlineData: { data: await base64EncodedDataPromise, mimeType: file.type }
      };

      const dbVariantSummary = (varianDatabase || []).map(v => {
        const sizesStr = (v.sizes || []).map(s => `${s.size}ml`).join(', ');
        return `- ${v.nama_product} (Pilihan ukuran: ${sizesStr || 'Standard'})`;
      }).join('\n');

      const prompt = `
Kamu adalah asisten OCR & AI Analisis Dokumen Pesanan / Repeat Order untuk pabrik maklon kosmetik & parfum Satala Dermatech.

Brand klien yang sedang diproses: "${namaBrand}"

Daftar Varian yang SUDAH TERDAFTAR di Database Brand "${namaBrand}":
${dbVariantSummary || '(Belum ada varian di database)'}

TUGAS UTAMA:
1. Baca teks, tabel, nota, invoice, atau screenshot chat WhatsApp di gambar yang diunggah.
2. Identifikasi setiap item produk/varian parfum yang dipesan klien:
   - Nama Varian (contoh: "Sun Zest", "Vanilla Mist", "Aroma Baccarat", dll)
   - Ukuran / Volume dalam ml jika ada (contoh: 20, 30, 50, 100)
   - Jumlah Pesanan / Qty dalam pcs (contoh: 500, 1000, 50, dll). Jika tidak tertulis jelas, default ke 1.
3. Cocokkan Nama Varian yang terbaca dengan Daftar Varian Database di atas:
   - Lakukan smart matching / fuzzy matching (misal: "Sunzest 20ml" -> cocok ke "Sun Zest", "SZ" -> cocok ke "Sun Zest", dll).
   - Jika cocok dengan salah satu di database, isi field "matched_variant_name" dengan nama persis yang ada di database.
   - Jika varian tersebut sama sekali tidak ada di database brand, kosongkan "matched_variant_name" (set null).
4. Catatan penting per baris (jika ada).

Keluarkan HANYA format JSON valid berikut tanpa markdown formatting tambahan atau pembungkus lain:
{
  "items": [
    {
      "raw_variant_name": "Nama varian yang tertulis di gambar",
      "matched_variant_name": "Nama varian di database jika cocok, atau null jika tidak ada",
      "ukuran_ml": 20,
      "qty": 500,
      "catatan": "keterangan tambahan jika ada"
    }
  ],
  "catatan_global": "catatan umum jika ada"
}
`;

      const responseText = await generateContentWithFallback(
        undefined,
        [prompt, inlineData]
      );

      const firstBrace = responseText.indexOf('{');
      const lastBrace = responseText.lastIndexOf('}');
      if (firstBrace === -1 || lastBrace === -1) {
        throw new Error('Respon AI tidak berisi objek JSON valid');
      }
      const jsonString = responseText.substring(firstBrace, lastBrace + 1);
      const parsed = JSON.parse(jsonString);

      let items = parsed.items || parsed.products || parsed.data || parsed.pesanan || (Array.isArray(parsed) ? parsed : []);
      handleAIItemsResult(items, 'Gambar PO', isAppend);
    } catch (err) {
      console.error('Scan Error:', err);
      toast.error('Gagal membaca gambar: ' + (err.message || 'Format tidak dikenali'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Process text/CSV table data with Fast Hybrid Matching (Instant Local + AI Fallback)
  const processSpreadsheetText = async (rawTableData, sourceLabel = 'Spreadsheet') => {
    if (!rawTableData || !rawTableData.trim()) {
      toast.error('Data spreadsheet kosong.');
      return;
    }

    setIsProcessing(true);
    setSheetSourceInfo(sourceLabel);

    try {
      // 1. FAST LOCAL PARSER FIRST (Instant 0.01s!)
      const structuredItems = parseStructuredTableDirectly(rawTableData);
      if (structuredItems.length > 0) {
        handleAIItemsResult(structuredItems, sourceLabel, false);
        setIsProcessing(false);
        return;
      }

      // 2. If unstructured, delegate to Gemini AI
      const dbVariantSummary = (varianDatabase || []).map(v => {
        const sizesStr = (v.sizes || []).map(s => `${s.size}ml`).join(', ');
        return `- ${v.nama_product} (Pilihan ukuran: ${sizesStr || 'Standard'})`;
      }).join('\n');

      const prompt = `
Kamu adalah asisten Analisis Spreadsheet & Dokumen Pesanan / Repeat Order untuk pabrik maklon kosmetik & parfum Satala Dermatech.

Brand klien yang sedang diproses: "${namaBrand}"

Daftar Varian yang SUDAH TERDAFTAR di Database Brand "${namaBrand}":
${dbVariantSummary || '(Belum ada varian di database)'}

DATA TABEL SPREADSHEET YANG DITERIMA:
"""
${rawTableData.slice(0, 20000)}
"""

TUGAS UTAMA:
1. Baca setiap baris data pesanan di tabel spreadsheet di atas.
2. Identifikasi:
   - Nama Varian / Produk (contoh: "Black Opium 30ml" -> Nama Varian: "Black Opium", Ukuran: 30)
   - Ukuran / Volume dalam ml (contoh: jika nama item "Black Opium 50ml", pisahkan nama varian "Black Opium" dan ukuran 50).
   - Jumlah Pesanan / Qty dalam pcs (contoh: 36, 200, 71, dll. Abaikan baris jika qty kosong atau 0).
   - Catatan (jika ada kolom keterangan).
3. Cocokkan Nama Varian yang terbaca dengan Database Brand.

Keluarkan HANYA format JSON valid berikut:
{
  "items": [
    {
      "raw_variant_name": "Nama varian di spreadsheet",
      "matched_variant_name": "Nama varian di database jika cocok, atau null",
      "ukuran_ml": 30,
      "qty": 36,
      "catatan": "keterangan jika ada"
    }
  ]
}
`;

      const responseText = await generateContentWithFallback(
        undefined,
        prompt
      );

      const firstBrace = responseText.indexOf('{');
      const lastBrace = responseText.lastIndexOf('}');
      if (firstBrace === -1 || lastBrace === -1) {
        throw new Error('Respon AI tidak berisi objek JSON valid');
      }
      const jsonString = responseText.substring(firstBrace, lastBrace + 1);
      const parsed = JSON.parse(jsonString);

      let items = parsed.items || parsed.products || parsed.data || parsed.pesanan || (Array.isArray(parsed) ? parsed : []);
      handleAIItemsResult(items, sourceLabel, false);
    } catch (err) {
      console.error('Spreadsheet parse error:', err);
      toast.error('Gagal menganalisis spreadsheet: ' + (err.message || 'Format tidak dikenali'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Google Spreadsheet URL Fetcher (with JSONP bypass for 100% CORS-free support)
  const processGoogleSheetUrl = async (urlToProcess) => {
    const targetUrl = (urlToProcess || sheetUrl || '').trim();
    if (!targetUrl) {
      toast.error('Masukkan link Google Spreadsheet terlebih dahulu.');
      return;
    }

    const matches = targetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (!matches || !matches[1]) {
      toast.error('Format link Google Spreadsheet tidak valid. Pastikan link diawali dengan https://docs.google.com/spreadsheets/d/...');
      return;
    }

    const sheetId = matches[1];
    const gidMatch = targetUrl.match(/[#&?]gid=([0-9]+)/);
    const gid = gidMatch ? gidMatch[1] : '0';

    setIsProcessing(true);
    toast.info('Mengambil data dari Google Sheets...');

    try {
      let tableDataText = '';
      try {
        const gvizTable = await fetchGoogleSheetJsonp(sheetId, gid);
        tableDataText = convertGvizTableToText(gvizTable);
      } catch (jsonpErr) {
        console.warn('JSONP fetch failed, attempting CSV fallback:', jsonpErr);
      }

      if (!tableDataText) {
        const exportUrls = [
          `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&gid=${gid}`,
          `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}`
        ];

        for (const expUrl of exportUrls) {
          try {
            const res = await fetch(expUrl);
            if (res.ok) {
              const txt = await res.text();
              if (txt && !txt.includes('<!DOCTYPE html>')) {
                tableDataText = txt;
                break;
              }
            }
          } catch (e) {
            console.warn('Fetch fallback error:', e);
          }
        }
      }

      if (tableDataText && tableDataText.trim().length > 5) {
        await processSpreadsheetText(tableDataText, 'Google Spreadsheet');
      } else {
        throw new Error('Google Spreadsheet belum dapat diakses. Pastikan akses di Google Sheets disetel "Siapa saja yang memiliki link dapat melihat".');
      }
    } catch (err) {
      console.warn('Google Sheet fetch error:', err);
      toast.error(err.message || 'Gagal mengakses link Google Sheets.');
      setIsProcessing(false);
    }
  };

  // Handle Excel / CSV File Upload
  const handleExcelFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    toast.info(`Membaca file ${file.name}...`);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const csvData = XLSX.utils.sheet_to_csv(worksheet);

        if (!csvData || !csvData.trim()) {
          throw new Error('File Excel tidak memiliki data tabel.');
        }

        processSpreadsheetText(csvData, file.name);
      } catch (err) {
        console.error('Excel parse error:', err);
        toast.error('Gagal membaca file Excel: ' + err.message);
        setIsProcessing(false);
      }
    };
    reader.onerror = () => {
      toast.error('Gagal membuka file.');
      setIsProcessing(false);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const loadAndProcessFile = (file, isAppend = false) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Mohon masukkan file gambar (JPG, PNG, WebP).');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const newImg = {
        id: `img-${Date.now()}-${Math.random()}`,
        url: reader.result,
        name: file.name || `Gambar ${imagePreviews.length + 1}`
      };

      if (isAppend) {
        setImagePreviews(prev => [...prev, newImg]);
      } else {
        setImagePreviews([newImg]);
      }

      processImageWithAI(file, isAppend);
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = (e, isAppend = false) => {
    const file = e.target.files?.[0];
    if (file) {
      loadAndProcessFile(file, isAppend);
      e.target.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
        setActiveTab('spreadsheet');
        const evt = { target: { files: [file], value: '' } };
        handleExcelFileUpload(evt);
      } else {
        setActiveTab('image');
        const shouldAppend = imagePreviews.length > 0;
        loadAndProcessFile(file, shouldAppend);
      }
    }
  };

  // Add a manual row directly
  const handleAddManualRow = () => {
    const defaultVarian = (varianDatabase && varianDatabase.length > 0) ? varianDatabase[0] : null;
    const defaultSize = defaultVarian?.sizes?.[0]?.size || 20;
    const isMatched = !!defaultVarian;
    const built = defaultVarian ? buildProductFromSize(defaultVarian, defaultSize, 100) : null;

    const newRow = {
      id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      selected: true,
      rawName: defaultVarian ? defaultVarian.nama_product : 'Varian Tambahan',
      rawSize: defaultSize,
      qty: 100,
      catatan: 'Ditambahkan manual',
      isMatched: isMatched,
      matchedVariantId: defaultVarian?.id || '',
      productData: built
    };

    setScannedItems(prev => [newRow, ...prev]);
    toast.success('Baris varian baru berhasil ditambahkan!');
  };

  // Handle manual variant mapping
  const handleMapVariant = (rowId, newVariantId) => {
    setScannedItems(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      const varian = varianDatabase.find(v => v.id === newVariantId);
      if (!varian) {
        return {
          ...row,
          isMatched: false,
          matchedVariantId: '',
          productData: null
        };
      }
      const built = buildProductFromSize(varian, row.rawSize, row.qty);
      toast.success(`Varian berhasil dicocokkan ke "${varian.nama_product}"!`);
      return {
        ...row,
        isMatched: true,
        matchedVariantId: varian.id,
        productData: built
      };
    }));
    setPickerActiveRowId(null);
  };

  // Handle Qty change
  const handleQtyChange = (rowId, newQty) => {
    const q = Math.max(1, parseInt(newQty) || 1);
    setScannedItems(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      let updatedProduct = row.productData;
      if (updatedProduct) {
        const unitPrice = updatedProduct.harga_jual_per_pcs || 0;
        updatedProduct = {
          ...updatedProduct,
          qty: q,
          total_harga_jual: unitPrice * q
        };
      }
      return { ...row, qty: q, productData: updatedProduct };
    }));
  };

  // Handle Price change directly
  const handlePriceChange = (rowId, newPrice) => {
    const p = Math.max(0, parseFloat(newPrice) || 0);
    setScannedItems(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      let updatedProduct = row.productData || {
        variant_id: row.matchedVariantId || 'manual',
        nama_product: row.rawName,
        is_discovery_set: false,
        qty: row.qty || 1,
        selected_size: row.rawSize || 20,
        racikan: []
      };
      updatedProduct = {
        ...updatedProduct,
        harga_jual_per_pcs: p,
        total_harga_jual: p * (row.qty || 1)
      };
      return { ...row, productData: updatedProduct };
    }));
  };

  // Handle Size change
  const handleSizeChange = (rowId, newSize) => {
    setScannedItems(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      const varian = varianDatabase.find(v => v.id === row.matchedVariantId);
      const built = varian ? buildProductFromSize(varian, newSize, row.qty) : row.productData;
      return { ...row, rawSize: newSize, productData: built };
    }));
  };

  // Toggle selection
  const toggleSelect = (rowId) => {
    setScannedItems(prev => prev.map(r => r.id === rowId ? { ...r, selected: !r.selected } : r));
  };

  // Toggle select all
  const toggleSelectAll = () => {
    const allSelected = scannedItems.every(r => r.selected);
    setScannedItems(prev => prev.map(r => ({ ...r, selected: !allSelected })));
  };

  // Remove row
  const removeRow = (rowId) => {
    setScannedItems(prev => prev.filter(r => r.id !== rowId));
  };

  // Remove image preview
  const removeImagePreview = (imgId) => {
    setImagePreviews(prev => prev.filter(img => img.id !== imgId));
  };

  // Calculated Totals
  const selectedRows = useMemo(() => scannedItems.filter(r => r.selected), [scannedItems]);
  const totalPcs = useMemo(() => selectedRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0), [selectedRows]);
  const totalVarianCount = selectedRows.length;
  const totalEstimasiHarga = useMemo(() => {
    return selectedRows.reduce((sum, r) => {
      return sum + (r.productData?.total_harga_jual || (r.productData?.harga_jual_per_pcs || 0) * r.qty || 0);
    }, 0);
  }, [selectedRows]);

  const matchedCount = useMemo(() => scannedItems.filter(r => r.isMatched).length, [scannedItems]);
  const unmatchedCount = useMemo(() => scannedItems.filter(r => !r.isMatched).length, [scannedItems]);

  // Filtered rows for the view
  const filteredScannedItems = useMemo(() => {
    return scannedItems.filter(item => {
      if (tableFilterTab === 'matched' && !item.isMatched) return false;
      if (tableFilterTab === 'unmatched' && item.isMatched) return false;
      if (tableSearchQuery) {
        const q = tableSearchQuery.toLowerCase();
        const raw = (item.rawName || '').toLowerCase();
        const matched = (item.productData?.nama_product || '').toLowerCase();
        return raw.includes(q) || matched.includes(q);
      }
      return true;
    });
  }, [scannedItems, tableFilterTab, tableSearchQuery]);

  // Filtered variants for the popup picker
  const filteredDatabaseVariants = useMemo(() => {
    if (!pickerQuery) return varianDatabase || [];
    const q = pickerQuery.toLowerCase().trim();
    return (varianDatabase || []).filter(v => v.nama_product.toLowerCase().includes(q));
  }, [varianDatabase, pickerQuery]);

  // Active item being picked
  const activePickerRow = useMemo(() => {
    return scannedItems.find(r => r.id === pickerActiveRowId);
  }, [scannedItems, pickerActiveRowId]);

  const handleApplyToPO = async () => {
    if (isSubmitting) return;
    const validProducts = selectedRows
      .filter(r => r.isMatched && r.productData)
      .map(r => r.productData);

    if (validProducts.length === 0) {
      toast.error('Tidak ada varian cocok yang dipilih untuk dimasukkan ke Repeat Order.');
      return;
    }

    try {
      setIsSubmitting(true);
      await onApply(validProducts);
      toast.success(`${validProducts.length} varian berhasil diterapkan ke form Repeat Order!`);
      onClose();
    } catch (e) {
      toast.error('Gagal menerapkan varian: ' + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div 
        className="bg-white border border-[#E6D5B8] rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hidden File Input for Appending Photos */}
        <input
          ref={appendFileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFileUpload(e, true)}
        />

        {/* Hidden File Input for Excel / CSV */}
        <input
          ref={excelFileInputRef}
          type="file"
          accept=".xlsx, .xls, .csv"
          className="hidden"
          onChange={handleExcelFileUpload}
        />

        {/* Header with Tab Switcher */}
        <div className="p-4 sm:p-5 bg-gradient-to-r from-[#F9F5EC] to-white border-b border-[#E6D5B8] flex items-center justify-between shrink-0 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#8C6208]/10 flex items-center justify-center border border-[#8C6208]/20">
              <Sparkles className="w-5 h-5 text-[#8C6208]" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-[#302621] flex items-center gap-2">
                Import Pesanan Repeat Order (AI)
                <span className="text-[11px] font-semibold bg-[#8C6208]/10 text-[#8C6208] px-2 py-0.5 rounded-full border border-[#8C6208]/20">
                  {namaBrand || 'Brand Klien'}
                </span>
              </h2>
              <p className="text-xs text-[#7A695B]">
                Ekstraksi otomatis nama varian, ukuran, dan qty via <b>Gambar PO</b> atau <b>Link Spreadsheet</b>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Tab Selector */}
            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button
                type="button"
                onClick={() => setActiveTab('image')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeTab === 'image' 
                    ? 'bg-white text-[#8C6208] shadow-xs' 
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <ImageIcon className="w-3.5 h-3.5" />
                Scan Gambar
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('spreadsheet')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeTab === 'spreadsheet' 
                    ? 'bg-white text-[#8C6208] shadow-xs' 
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                Link Spreadsheet / Excel
              </button>
            </div>

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
          {/* TAB 1: SCAN GAMBAR */}
          {activeTab === 'image' && (
            <>
              {imagePreviews.length === 0 ? (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center cursor-pointer transition-all ${
                    isDragging 
                      ? 'border-[#8C6208] bg-[#8C6208]/5 scale-[0.99]' 
                      : 'border-slate-300 hover:border-[#8C6208]/60 bg-slate-50/50 hover:bg-[#F9F5EC]/40'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, false)}
                  />
                  <div className="w-12 h-12 rounded-2xl bg-white shadow-xs border border-slate-200 flex items-center justify-center mx-auto mb-2.5 text-[#8C6208]">
                    <Upload className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-bold text-[#302621] mb-1">
                    Pilih atau Tarik Gambar PO / Chat ke Sini
                  </h3>
                  <p className="text-xs text-[#7A695B] max-w-md mx-auto mb-3">
                    Mendukung file JPG, PNG, screenshot nota pemesanan, atau <b>tekan Ctrl + V</b> langsung dari clipboard Anda.
                  </p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#E6D5B8] text-xs font-semibold text-[#8C6208] shadow-2xs">
                    <Copy className="w-3.5 h-3.5" /> Shortcut: Tekan Ctrl + V di mana saja
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-[#8C6208]" />
                      <span className="text-xs font-bold text-slate-800">
                        Foto Terpilih ({imagePreviews.length} Gambar)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => appendFileInputRef.current?.click()}
                        disabled={isProcessing}
                        className="h-7 text-xs bg-[#8C6208] hover:bg-[#725007] text-white rounded-lg shadow-xs"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Scan Foto Tambahan
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setImagePreviews([]);
                          setScannedItems([]);
                        }}
                        disabled={isProcessing}
                        className="h-7 text-xs border-slate-200 hover:bg-slate-100"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reset
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    {imagePreviews.map((img) => (
                      <div 
                        key={img.id}
                        className="relative group shrink-0 w-12 h-12 rounded-lg bg-slate-200 border border-slate-300 overflow-hidden cursor-pointer shadow-2xs"
                      >
                        <img 
                          src={img.url} 
                          alt={img.name} 
                          className="w-full h-full object-cover"
                          onClick={() => setSelectedPreviewImage(img.url)}
                        />
                        <div 
                          onClick={() => setSelectedPreviewImage(img.url)}
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                        >
                          <ZoomIn className="w-3.5 h-3.5 text-white" />
                        </div>
                        {imagePreviews.length > 1 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImagePreview(img.id);
                            }}
                            className="absolute top-0.5 right-0.5 w-3.5 h-3.5 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* TAB 2: SPREADSHEET & EXCEL */}
          {activeTab === 'spreadsheet' && (
            <div className="space-y-3">
              {/* Option A: Link Google Sheets */}
              <div className="p-3.5 bg-[#F9F5EC]/60 border border-[#E6D5B8] rounded-2xl space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-[#302621] flex items-center gap-2">
                    <Link className="w-4 h-4 text-[#8C6208]" />
                    <span>Masukkan Link Google Spreadsheet</span>
                  </label>
                  <span className="text-[10px] text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                    Bebas Hambatan CORS • Instan
                  </span>
                </div>

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      placeholder="Paste link Google Spreadsheet di sini..."
                      value={sheetUrl}
                      onChange={(e) => setSheetUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          processGoogleSheetUrl(sheetUrl);
                        }
                      }}
                      className="bg-white border-slate-300 focus-visible:ring-[#8C6208] text-xs h-9 pr-8"
                    />
                    {sheetUrl && (
                      <button
                        type="button"
                        onClick={() => setSheetUrl('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  <Button
                    type="button"
                    onClick={() => processGoogleSheetUrl(sheetUrl)}
                    disabled={isProcessing || !sheetUrl.trim()}
                    className="h-9 bg-[#8C6208] hover:bg-[#725007] text-white text-xs font-bold px-4 rounded-xl shadow-xs shrink-0"
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Membaca...
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Baca Sheet
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              {/* Option B: Upload Excel or Paste Table Rows */}
              {scannedItems.length === 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div 
                    onClick={() => excelFileInputRef.current?.click()}
                    className="p-3 bg-slate-50 hover:bg-slate-100/80 border border-slate-200 hover:border-[#8C6208]/50 rounded-2xl cursor-pointer transition-all flex items-center gap-3 text-left group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <FileSpreadsheet className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">Upload File Excel / CSV</h4>
                      <p className="text-[10px] text-slate-500">Mendukung format .xlsx, .xls, .csv</p>
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Table className="w-3.5 h-3.5 text-[#8C6208]" /> Salin-Tempel Baris Tabel
                      </span>
                      {pastedTableText && (
                        <button
                          type="button"
                          onClick={() => setPastedTableText('')}
                          className="text-[10px] text-slate-400 hover:text-red-600"
                        >
                          Bersihkan
                        </button>
                      )}
                    </div>
                    <Textarea
                      placeholder="Copy baris tabel lalu paste di sini (Ctrl+V)..."
                      value={pastedTableText}
                      onChange={(e) => setPastedTableText(e.target.value)}
                      className="text-xs resize-none h-14 bg-white border-slate-200 focus-visible:ring-[#8C6208]"
                    />
                    {pastedTableText.trim() && (
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => processSpreadsheetText(pastedTableText, 'Tabel Salinan Manual')}
                        disabled={isProcessing}
                        className="mt-2 h-7 text-[11px] bg-[#8C6208] hover:bg-[#725007] text-white font-bold rounded-lg"
                      >
                        Analisis Tabel Ini
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Processing Loader */}
          {isProcessing && (
            <div className="p-6 text-center bg-slate-50/50 rounded-2xl border border-slate-200 space-y-2 animate-pulse">
              <Loader2 className="w-7 h-7 text-[#8C6208] animate-spin mx-auto" />
              <h4 className="text-xs font-bold text-[#302621]">ASA AI Sedang Menganalisis Dokumen...</h4>
              <p className="text-[11px] text-[#7A695B] max-w-sm mx-auto">
                Mengekstrak nama varian, ukuran, kuantiti, dan mencocokkannya ke database brand "{namaBrand}".
              </p>
            </div>
          )}

          {/* Summary Metric Cards */}
          {!isProcessing && scannedItems.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
              <div className="bg-[#F9F5EC] p-2.5 rounded-xl border border-[#E6D5B8]">
                <span className="text-[10px] font-bold text-[#7A695B] uppercase block">Total Varian</span>
                <span className="text-base font-black text-[#8C6208]">
                  {totalVarianCount} <span className="text-xs font-normal">Varian</span>
                </span>
              </div>
              <div className="bg-[#F9F5EC] p-2.5 rounded-xl border border-[#E6D5B8]">
                <span className="text-[10px] font-bold text-[#7A695B] uppercase block">Total Pcs</span>
                <span className="text-base font-black text-[#302621]">
                  {fmt(totalPcs)} <span className="text-xs font-normal">Pcs</span>
                </span>
              </div>
              <div className="bg-[#F9F5EC] p-2.5 rounded-xl border border-[#E6D5B8]">
                <span className="text-[10px] font-bold text-[#7A695B] uppercase block">Estimasi Nilai</span>
                <span className="text-base font-black text-emerald-700 truncate block">
                  Rp {fmt(totalEstimasiHarga)}
                </span>
              </div>
              <div className="bg-white p-2.5 rounded-xl border border-slate-200 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Status Kecocokan</span>
                  <div className="text-xs font-bold flex items-center gap-1.5">
                    <span className="text-emerald-700">{matchedCount} Cocok</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-amber-700">{unmatchedCount} Perlu Dicek</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scanned Items Table */}
          {!isProcessing && scannedItems.length > 0 && (
            <div className="border border-slate-200 rounded-2xl bg-white shadow-xs overflow-hidden">
              {/* Table Filter Toolbar */}
              <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                {/* Status Filter Tabs */}
                <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
                  <button
                    type="button"
                    onClick={() => setTableFilterTab('all')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                      tableFilterTab === 'all' 
                        ? 'bg-[#8C6208] text-white shadow-2xs' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Semua ({scannedItems.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setTableFilterTab('matched')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                      tableFilterTab === 'matched' 
                        ? 'bg-emerald-600 text-white shadow-2xs' 
                        : 'text-emerald-700 hover:bg-emerald-50'
                    }`}
                  >
                    Cocok ({matchedCount})
                  </button>
                  {unmatchedCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setTableFilterTab('unmatched')}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                        tableFilterTab === 'unmatched' 
                          ? 'bg-amber-600 text-white shadow-2xs' 
                          : 'text-amber-700 hover:bg-amber-50'
                      }`}
                    >
                      Belum Terdaftar ({unmatchedCount})
                    </button>
                  )}
                </div>

                {/* Quick Search */}
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Cari nama varian..."
                    value={tableSearchQuery}
                    onChange={(e) => setTableSearchQuery(e.target.value)}
                    className="h-8 text-xs pl-8 pr-7 bg-white rounded-lg border-slate-200 focus-visible:ring-[#8C6208]"
                  />
                  {tableSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setTableSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleAddManualRow}
                    className="h-8 text-xs bg-white text-slate-700 font-bold border-slate-200 rounded-lg shadow-2xs"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1 text-[#8C6208]" /> Tambah Manual
                  </Button>
                </div>
              </div>

              {/* Table Body Area */}
              <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="p-3 w-8 text-center">
                        <input
                          type="checkbox"
                          checked={scannedItems.length > 0 && scannedItems.every(r => r.selected)}
                          onChange={toggleSelectAll}
                          className="rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208] cursor-pointer"
                        />
                      </th>
                      <th className="p-3 min-w-[150px]">Nama Varian di Dokumen</th>
                      <th className="p-3 min-w-[240px]">Pencocokan Database Brand</th>
                      <th className="p-3 w-28">Ukuran (ml)</th>
                      <th className="p-3 w-24">Qty (Pcs)</th>
                      <th className="p-3 w-32 text-right">Harga Jual / Pcs</th>
                      <th className="p-3 w-32 text-right">Subtotal</th>
                      <th className="p-3 w-10 text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredScannedItems.map((item) => (
                      <ScannedRowItem
                        key={item.id}
                        item={item}
                        varianDatabase={varianDatabase}
                        onToggleSelect={toggleSelect}
                        onOpenPicker={(id) => {
                          setPickerActiveRowId(id);
                          setPickerQuery('');
                        }}
                        onSizeChange={handleSizeChange}
                        onQtyChange={handleQtyChange}
                        onPriceChange={handlePriceChange}
                        onRemove={removeRow}
                      />
                    ))}
                    {filteredScannedItems.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-slate-400 text-xs">
                          Tidak ada varian yang cocok dengan filter pencarian "{tableSearchQuery}".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table Footer Stats */}
              <div className="p-2.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
                <span>
                  Menampilkan <b>{filteredScannedItems.length}</b> dari <b>{scannedItems.length}</b> varian
                </span>
                <span>
                  * Klik pada nama varian database jika ingin mengganti varian secara manual
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-bold rounded-xl"
          >
            Tutup
          </Button>

          {scannedItems.length > 0 && (
            <Button
              onClick={handleApplyToPO}
              disabled={isSubmitting || isProcessing || selectedRows.filter(r => r.isMatched).length === 0}
              className="bg-[#8C6208] hover:bg-[#725007] text-white text-xs font-bold rounded-xl px-5 shadow-md shadow-[#8C6208]/20"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Menerapkan...
                </>
              ) : (
                <>
                  Terapkan ke Repeat Order ({selectedRows.filter(r => r.isMatched).length} Varian Cocok)
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Modern Searchable Variant Picker Dialog */}
      {pickerActiveRowId && (
        <div 
          className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => setPickerActiveRowId(null)}
        >
          <div 
            className="bg-white rounded-2xl border border-[#E6D5B8] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Picker Header */}
            <div className="p-4 bg-[#F9F5EC] border-b border-[#E6D5B8] flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-[#302621] flex items-center gap-2">
                  <Search className="w-4 h-4 text-[#8C6208]" />
                  Pilih Varian Database "{namaBrand}"
                </h4>
                {activePickerRow && (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Nama di dokumen: <b className="text-slate-800">{activePickerRow.rawName}</b> ({activePickerRow.rawSize}ml)
                  </p>
                )}
              </div>
              <button 
                type="button"
                onClick={() => setPickerActiveRowId(null)}
                className="w-7 h-7 rounded-full hover:bg-slate-200/60 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search Input Box */}
            <div className="p-3 border-b border-slate-100 bg-white">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  ref={pickerInputRef}
                  placeholder="Ketik nama varian (contoh: Coffee, Vanilla, Baccarat)..."
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  className="pl-9 pr-8 text-xs bg-slate-50 border-slate-200 focus-visible:ring-[#8C6208] h-10 rounded-xl"
                />
                {pickerQuery && (
                  <button
                    type="button"
                    onClick={() => setPickerQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* List of Database Variants */}
            <div className="p-2 overflow-y-auto flex-1 divide-y divide-slate-100">
              {filteredDatabaseVariants.map(v => {
                const sizesStr = (v.sizes || []).map(s => `${s.size}ml`).join(', ');
                const isCurrentMatch = activePickerRow?.matchedVariantId === v.id;

                return (
                  <div
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMapVariant(pickerActiveRowId, v.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleMapVariant(pickerActiveRowId, v.id);
                      }
                    }}
                    className={`w-full text-left p-3.5 rounded-2xl hover:bg-[#F9F5EC] transition-all flex items-center justify-between cursor-pointer group border select-none ${
                      isCurrentMatch 
                        ? 'bg-[#F9F5EC] border-[#8C6208]/50 shadow-xs' 
                        : 'bg-white border-slate-200/80 hover:border-[#8C6208]/40 shadow-2xs'
                    }`}
                  >
                    <div className="space-y-1">
                      <span className={`text-xs font-bold block transition-colors ${
                        isCurrentMatch ? 'text-[#8C6208]' : 'text-slate-800 group-hover:text-[#8C6208]'
                      }`}>
                        {v.nama_product}
                      </span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {(v.sizes || []).map(s => (
                          <span 
                            key={s.size}
                            className="text-[10px] font-semibold bg-slate-50 group-hover:bg-white px-2 py-0.5 rounded-md border border-slate-200 text-slate-600"
                          >
                            {s.size}ml
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="shrink-0 ml-3 pointer-events-none">
                      {isCurrentMatch ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#8C6208] bg-white px-3 py-1.5 rounded-xl border border-[#8C6208]/30 shadow-2xs">
                          <Check className="w-3.5 h-3.5" /> Terpilih
                        </span>
                      ) : (
                        <span className="text-[11px] font-bold text-[#8C6208] bg-[#F9F5EC] group-hover:bg-[#8C6208] group-hover:text-white px-3.5 py-1.5 rounded-xl border border-[#8C6208]/20 transition-all shadow-2xs">
                          Pilih Varian Ini →
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {filteredDatabaseVariants.length === 0 && (
                <div className="p-8 text-center space-y-2">
                  <Package className="w-8 h-8 text-slate-300 mx-auto" />
                  <p className="text-xs text-slate-500 font-medium">
                    Tidak ditemukan varian database dengan kata kunci "<b>{pickerQuery}</b>".
                  </p>
                </div>
              )}
            </div>

            {/* Picker Footer */}
            <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
              <span>Menampilkan {filteredDatabaseVariants.length} varian brand {namaBrand}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPickerActiveRowId(null)}
                className="h-7 text-xs border-slate-200"
              >
                Batal
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Preview Dialog */}
      {selectedPreviewImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedPreviewImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-slate-300"
            onClick={() => setSelectedPreviewImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={selectedPreviewImage} 
            alt="Fullscreen Document" 
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl" 
          />
        </div>
      )}
    </div>
  );
}
